import * as React from "react";
import _ from "underscore";
import styled, { css } from "styled-components";
import PropTypes from "prop-types";
import getNestedValue from "lodash/get";

import Alert from "pages/common/components/alert/alert";
import Icon from "pages/common/components/decorations/icons/icon";
import Tooltip from "pages/common/components/tooltip/tooltip";
import { backboneViewAsReactComponent } from "util/react-utils";
import { SRT_LATENCY_RANGE } from "pages/producer/common/store/input/constants";
import { SourceProtocol } from "pages/producer/common/store/input/types";

export const FieldWrapper = styled.div``;
export const StyledLabel = styled.label`
    font-size: 0.84615em;
    color: #596281;
    text-transform: uppercase;
    margin-top: 1em;
    position: relative;
    margin-bottom: 5px;
    display: flex;
    align-items: center;

    i {
        font-size: 1.5em;
    }

    span {
        ${(props) => {
            if (props.required) {
                return css`
                    &:after {
                        content: "*";
                        color: red;
                        margin: 0 3px;
                    }
                `;
            }
        }}
    }
`;

const wrapInputComponent = (
    FieldComponent,
    {
        allowOverflow = false,
        showLabel = true,
        fixLabel = false,
        fixId = false
    } = {}
) => {
    class WrappedInput extends React.Component {
        constructor(props) {
            super(props);
            this.inputId = `${this.props.name}-${
                Math.floor(Math.random() * 9999) + 1
            }`;
        }

        render() {
            const {
                label,
                name,
                errors = {},
                values = {},
                placeholders = {},
                touched = {},
                handleChange,
                handleBlur,
                handleFocus = () => {},
                tooltip = null,
                required,
                className,
                showDefaultLabel
            } = this.props;

            const error = getNestedValue(errors, name);
            const value = getNestedValue(values, name);
            const placeholder = getNestedValue(placeholders, name);
            const labelText = label ? label : !fixLabel ? name : null;
            const hasBeenTouched = getNestedValue(touched, name);
            const _showLabel =
                showDefaultLabel === undefined ? showLabel : showDefaultLabel;

            let containerClass = `grForm__field`;
            allowOverflow &&
                (containerClass += ` grForm__field--allowOverflow`);

            let labelClass = `grForm__label`;
            required && (labelClass += ` grForm__label--required`);

            // The select component adds the id attribute to the div rather than the input so using this to make it add the attribute on the correct element
            let addId = true;

            // need this or label will be shown twice - can't use showLabel prop as tooltip won't show
            if (fixLabel) {
                this.newProps = { ...this.props };
                delete this.newProps.label;

                if (fixId) {
                    this.newProps.inputId = this.inputId;
                    addId = false;
                }
            } else {
                this.newProps = { ...this.props };
            }

            return (
                <FieldWrapper className={containerClass}>
                    {_showLabel && labelText && (
                        <StyledLabel required={required} htmlFor={this.inputId}>
                            <span>
                                {labelText}:{` `}
                            </span>
                            {tooltip && (
                                <Tooltip {...tooltip}>
                                    <Icon name="info" />
                                </Tooltip>
                            )}
                        </StyledLabel>
                    )}
                    <FieldComponent
                        value={value}
                        placeholder={placeholder}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        hasBeenTouched={hasBeenTouched}
                        className={className}
                        id={addId ? this.inputId : null}
                        {...this.newProps}
                    />
                    {error && hasBeenTouched && (
                        <Alert type={`error`}>{error}</Alert>
                    )}
                </FieldWrapper>
            );
        }
    }

    WrappedInput.propTypes = {
        required: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        label: PropTypes.string,
        name: PropTypes.string,
        error: PropTypes.string
    };

    return WrappedInput;
};

const prepareBackboneEditor = (constructor, props) => {
    const { name, setFieldTouched, setFieldValue } = props;

    const extendedEditor = constructor.extend({
        events: {
            change: function () {
                setFieldValue(name, this.getValue());
                this.trigger(`change`, this);
            }
        },

        initialize: function (options) {
            extendedEditor.__super__.initialize.call(this, {
                ...options,
                key: options.reactKey
            });
        },

        render: function (props, { shouldCallSetFieldValue = true }) {
            extendedEditor.__super__.render.apply(this, arguments);

            if (this.value && shouldCallSetFieldValue) {
                this.setValue(this.value);
            }
            return this;
        },

        blur: function () {
            extendedEditor.__super__.blur.apply(this, arguments);
            setFieldTouched(name, true);
        },

        validate: _.noop,
        getValidator: _.noop
    });

    return extendedEditor;
};

const wrapBackboneEditor = (editor, extender) => {
    class EditorComponent extends React.Component {
        constructor(props) {
            super(props);
            let extendedEditor = prepareBackboneEditor(editor, props);

            if (_.isFunction(extender)) {
                extendedEditor = extender(extendedEditor, props);
            }

            const editorOptions = { ...this.props, reactKey: this.props.name };
            this.editorRef = React.createRef();

            this.constructedWrappedComponent = backboneViewAsReactComponent(
                extendedEditor,
                editorOptions
            );
        }

        shouldComponentUpdate(nextProps) {
            if (
                this.editorRef.current &&
                this.props.value !== nextProps.value
            ) {
                this.editorRef.current.view.setValue(nextProps.value);
            }
            return false;
        }

        render() {
            const WrappedComponent = this.constructedWrappedComponent;

            return (
                <WrappedComponent
                    ref={this.editorRef}
                    {...this.props}
                    reactKey={this.props.name}
                />
            );
        }
    }

    return EditorComponent;
};

const setInitialSelectOption = (Option, optionType) => {
    return Object.values(Option).find((type) => optionType === type.value);
};

const REGEX_CHECKS = {
    streamUrlNoSpace: /^(http(s)?|rtmp(e)?|srt):\/\/(\S*$)/,
    streamUrlIpAddress:
        /^(http(s)?|rtmp(e)?|srt):\/\/\d{1,3}(\.\d{1,3}){3}(:(\d){1,5})?/,
    streamUrlDomainName:
        /^(http(s)?|rtmp(e)?|srt):\/\/[\w\.-]+(\.[a-zA-Z]{2,})(:\d+)?\/?/
};

// to replace the old object with this once full feature upgrade is ready
const UPDATED_REGEX_CHECKS = {
    streamUrlNoSpace: /^(http(s)?|rtmp(e)?):\/\/(\S*$)/,
    streamRtmpUrlIpAddress:
        /^(rtmp(e)?):\/\/\d{1,3}(\.\d{1,3}){3}(:(\d){1,5})?\//,
    streamRtmpUrlDomainName: /^(rtmp(e)?):\/\/[\w.-]+(\.[a-zA-Z]{2,})(:\d+)?\//,
    streamHlsUrlIpAddress:
        /^(http(s)?):\/\/\d{1,3}(\.\d{1,3}){3}(:(\d){1,5})?\//,
    streamHlsUrlDomainName: /^(http(s)?):\/\/[\w.-]+(\.[a-zA-Z]{2,})(:\d+)?\//,
    streamSrtHostName: /^[^\s:\/?#]+$/
};

const isValidStreamUrl = (url, type) => {
    if (type) {
        if (type === SourceProtocol.RTMP) {
            return (
                UPDATED_REGEX_CHECKS.streamUrlNoSpace.test(url) &&
                (UPDATED_REGEX_CHECKS.streamRtmpUrlIpAddress.test(url) ||
                    UPDATED_REGEX_CHECKS.streamRtmpUrlDomainName.test(url))
            );
        }

        if (type === SourceProtocol.HLS) {
            return (
                UPDATED_REGEX_CHECKS.streamUrlNoSpace.test(url) &&
                (UPDATED_REGEX_CHECKS.streamHlsUrlIpAddress.test(url) ||
                    UPDATED_REGEX_CHECKS.streamHlsUrlDomainName.test(url))
            );
        }

        if (type === SourceProtocol.SRT) {
            return UPDATED_REGEX_CHECKS.streamSrtHostName.test(url);
        }
    } else {
        return (
            REGEX_CHECKS.streamUrlNoSpace.test(url) &&
            (REGEX_CHECKS.streamUrlIpAddress.test(url) ||
                REGEX_CHECKS.streamUrlDomainName.test(url))
        );
    }
};

const sliceFullSrtIpAddress = (ip) => {
    // srt URLs do not work with javascripts URL method,
    // replacing srt with https is a tad hacky but lets us pick each URL param easily
    const validateUrl = ip.replace(`srt`, `https`);
    let slicedParams;

    try {
        const url = new URL(validateUrl);
        slicedParams = {
            ipAddress: url.hostname,
            latency:
                url.searchParams.get(`latency`) || SRT_LATENCY_RANGE.DEFAULT,
            streamId:
                url.searchParams.get(`streamId`) ||
                url.searchParams.get(`streamid`),
            port: url.port
        };
    } catch (e) {
        console.log(e);
    }

    return slicedParams;
};

export {
    wrapInputComponent,
    wrapBackboneEditor,
    setInitialSelectOption,
    isValidStreamUrl,
    sliceFullSrtIpAddress
};
